import React, { useEffect, useRef, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { groupBy } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useViewport } from 'components/templates/viewportProvider';
import { OverlayPortal } from 'components/templates/overlayPortal';
import { OverlayModal } from 'components/templates/overlayModal';
import SortDropDown from 'components/atoms/sortDropdown';
import PlayIcon from 'assets/images/play-icon-v1.png';
import { EventSelector } from 'modules/event/selector';
import { EventActions } from 'modules/event/actions';
//eslint-disable-next-line
import styles from "./styles.scss";

const PAGE_SIZE = 25;

const PastEventGrid = props => {
  const {
    pastEvents,
    hasMore,
    loadingMore,
    pagination,
    activeFilterIds,
    activeFilters,
    isFiltered,
    getPastEvents,
  } = props;

  const { height } = useViewport();
  const sentinelRef = useRef(null);

  const flattenedData = (pastEvents || []).reduce(
    (acc, item) =>
      acc.concat(
        item.date.map(date => Object.assign({}, item, { date: date }))
      ),
    []
  );

  const groupData = key => {
    if (key === 'date') {
      const dateSort = [...flattenedData].sort((a, b) => new Date(b.date) - new Date(a.date));
      return groupBy(dateSort, key);
    }
    return groupBy(flattenedData, key);
  };

  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [sortOption, selectSortOption] = useState(options[0]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [key, setKey] = useState('date');
  const [group, createGroup] = useState(() => groupData('date'));

  useEffect(() => {
    createGroup(groupData(key));
  }, [pastEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref keeps latest values so the scroll listener never needs to be recreated.
  const loadMoreState = useRef({});
  useEffect(() => {
    loadMoreState.current = {
      hasMore,
      loadingMore,
      pagination,
      activeFilterIds,
      getPastEvents,
    };
  }, [hasMore, loadingMore, pagination, activeFilterIds, getPastEvents]);

  // Set up scroll listener once. SimpleBar's scrollable element is
  // .simplebar-content-wrapper — we find it at mount time via closest().
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollContainer = sentinel.closest('.simplebar-content-wrapper');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollHeight - scrollTop - clientHeight < 600) {
        const { hasMore, loadingMore, pagination, activeFilterIds, getPastEvents } = loadMoreState.current;
        if (hasMore && !loadingMore && getPastEvents) {
          getPastEvents(pagination.page + 1, PAGE_SIZE, {
            venueIds: (activeFilterIds && activeFilterIds.venue) || [],
            cityIds: (activeFilterIds && activeFilterIds.city) || [],
            genreIds: (activeFilterIds && activeFilterIds.genre) || [],
            artistIds: (activeFilterIds && activeFilterIds.artist) || [],
          });
        }
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelect = (option) => {
    if (option.value.includes('SORT BY MOST RECENT')) {
      createGroup(groupData('date'));
      setKey('date');
    } else if (option.value.includes('SORT BY GENRE')) {
      createGroup(groupData('genre'));
      setKey('genre');
    } else if (option.value.includes('SORT BY ARTIST')) {
      createGroup(groupData('artistName'));
      setKey('artistName');
    } else if (option.value.includes('SORT BY VENUE')) {
      createGroup(groupData('venue'));
      setKey('venue');
    }
    selectSortOption(option.value);
  };

  const orderedData = sortOption === options[0]
    ? Object.keys(group)
    : Object.keys(group).sort();

  const showOverlay = showVideoOverlay ? (
    <OverlayPortal>
      <OverlayModal
        data={videoUrl}
        theme={'dark-theme'}
        type={'video-overlay'}
        className={'past-event-video'}
        onClosePress={() => setShowVideoOverlay(false)}
        breadcrumbList={[]}
        activeFilters={activeFilters}
      />
    </OverlayPortal>
  ) : null;

  return (
    <div className={'past-events-grid'} style={{ height }}>
      <div className={'header'}>
        <h2>Music Foundation Events Library</h2>
      </div>
      <div className={'sort-info'}>
        <div className={'dropdown'}>
          <SortDropDown
            options={options}
            onSelect={option => onSelect(option)}
            value={sortOption}
            placeholder={sortOption}
            mode={'transparent'}
          />
        </div>
      </div>
      <div className={'grid-container'}>
        {isFiltered && (!pastEvents || pastEvents.length === 0) ? (
          <div className={'no-events-found'}>
            <h2 className={'no-events-title'}>No Events Found</h2>
            <p className={'no-events-subtitle'}>Try adjusting your filters to discover more events.</p>
          </div>
        ) : (
          <>
            {orderedData.map((groupKey, idx) => {
              const card = group[groupKey].sort();
              const formatted_data = groupKey.split(',').join(', ');
              return (
                <div className={'group'} key={idx}>
                  <div className={'group-info'}>
                    <span className={'info-type'}>{formatted_data}</span>
                  </div>
                  <Row className={'grid-group-content'}>
                    {card.map((list, cardIdx) => (
                      <Col key={cardIdx} md={4} className={'past-events-card'}>
                        <div className={'past-event-card-container'}>
                          <div className={'video-section'}>
                            {list.videoUrl ? (
                              <div style={{ height: '100%' }}>
                                <img
                                  className={'video-thumb'}
                                  src={list.videoThumbNail || list.image}
                                  alt={''}
                                />
                                <img
                                  className={'play'}
                                  src={PlayIcon}
                                  alt={''}
                                  onClick={() => {
                                    setVideoUrl(list.videoUrl);
                                    setShowVideoOverlay(true);
                                  }}
                                />
                              </div>
                            ) : (
                              <img
                                className={'video-thumb'}
                                src={list.image}
                                alt={''}
                              />
                            )}
                          </div>
                          <div className={'content'}>
                            <h3 className={'name'}>{list.name}</h3>
                            <div className={'details'}>
                              <div className={'left'}>
                                <span className={'genre'}>{list.genre}</span>
                                <span className={'location'}>{list.venue}</span>
                              </div>
                              <div className={'right'}>
                                <span className={'duration'}>
                                  {list.durationCheck && list.videoDuration}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              );
            })}
            {loadingMore && (
              <div className={'load-more-spinner'}>
                <span>Loading more...</span>
              </div>
            )}
          </>
        )}
        <div ref={sentinelRef} className={'scroll-sentinel'} />
      </div>
      {showOverlay}
    </div>
  );
};

const mapStateToProps = state => ({
  pastEvents: EventSelector.getPastEvents(state),
  hasMore: EventSelector.hasMorePastEvents(state),
  loadingMore: EventSelector.getPastEventsLoadingMore(state),
  pagination: EventSelector.getPastEventsPagination(state),
  activeFilterIds: EventSelector.getActiveEventFilters(state),
  activeFilters: EventSelector.getActiveEventFiltersNames(state),
  isFiltered: EventSelector.isEventsFiltered(state),
});

const mapDispatchToProps = dispatch => {
  const { getPastEvents } = EventActions;
  return bindActionCreators({ getPastEvents }, dispatch);
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(PastEventGrid);

export default withRouter(connectedComponent);

const options = [
  'SORT BY MOST RECENT',
  'SORT BY GENRE',
  'SORT BY ARTIST',
  'SORT BY VENUE',
];
